import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, 
  RefreshCw,
  Calculator,
  PieChart,
  FileSpreadsheet,
  Download,
  Printer,
  Calendar,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Check,
  X,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';

const FinancialReports = ({ user }) => {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(reportType || 'trial-balance');
  const [reportData, setReportData] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [selectedLedgerId, setSelectedLedgerId] = useState('');
  
  // Date filter states
  const [filterType, setFilterType] = useState('custom'); // 'custom', 'month', 'fy'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFY, setSelectedFY] = useState('');
  
  // Financial years
  const currentYear = new Date().getFullYear();
  const financialYears = [
    { value: `${currentYear - 1}-${currentYear}`, label: `FY ${currentYear - 1}-${currentYear}` },
    { value: `${currentYear}-${currentYear + 1}`, label: `FY ${currentYear}-${String(currentYear + 1).slice(-2)}` },
    { value: `${currentYear + 1}-${currentYear + 2}`, label: `FY ${currentYear + 1}-${String(currentYear + 2).slice(-2)}` },
  ];
  
  // Months
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  // Calculate dates based on filter type
  const getDateRange = () => {
    if (filterType === 'month') {
      const start = new Date(selectedYear, selectedMonth - 1, 1);
      const end = new Date(selectedYear, selectedMonth, 0);
      return {
        from: start.toISOString().split('T')[0],
        to: end.toISOString().split('T')[0],
        asOf: end.toISOString().split('T')[0]
      };
    } else if (filterType === 'fy' && selectedFY) {
      const [startYear, endYear] = selectedFY.split('-').map(Number);
      // Indian FY: April 1 to March 31
      return {
        from: `${startYear}-04-01`,
        to: `${endYear}-03-31`,
        asOf: `${endYear}-03-31`
      };
    } else {
      return {
        from: fromDate,
        to: toDate,
        asOf: asOfDate
      };
    }
  };

  useEffect(() => {
    if (reportType) {
      setActiveTab(reportType);
    }
    fetchReport(reportType || 'trial-balance');
    fetchLedgers();
  }, [reportType]);

  const fetchReport = async (type) => {
    try {
      setLoading(true);
      let endpoint = '';
      const dateRange = getDateRange();
      
      switch (type) {
        case 'trial-balance':
          endpoint = `/finance/reports/trial-balance?as_of_date=${dateRange.asOf}`;
          break;
        case 'profit-loss':
          endpoint = `/finance/reports/profit-loss?from_date=${dateRange.from}&to_date=${dateRange.to}`;
          break;
        case 'balance-sheet':
          endpoint = `/finance/reports/balance-sheet?as_of_date=${dateRange.asOf}`;
          break;
        case 'ledger-statement':
          if (!selectedLedgerId) {
            setReportData(null);
            setLoading(false);
            return;
          }
          endpoint = `/finance/reports/ledger-statement/${selectedLedgerId}?from_date=${dateRange.from}&to_date=${dateRange.to}`;
          break;
        default:
          endpoint = `/finance/reports/trial-balance?as_of_date=${dateRange.asOf}`;
      }
      
      const response = await api.get(endpoint);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLedgers = async () => {
    try {
      const response = await api.get('/finance/ledgers');
      setLedgers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ledgers:', error);
    }
  };

  const fetchLedgerStatement = async () => {
    if (!selectedLedgerId) {
      toast.error('Please select a ledger');
      return;
    }
    setActiveTab('ledger-statement');
    fetchReport('ledger-statement');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/finance/reports/${tab}`);
    if (tab !== 'ledger-statement') {
      fetchReport(tab);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!reportData) {
      toast.error('No report data to export');
      return;
    }
    
    let csvContent = '';
    const dateRange = getDateRange();
    
    switch (activeTab) {
      case 'trial-balance':
        csvContent = 'Trial Balance Report\n';
        csvContent += `As of Date: ${dateRange.asOf}\n\n`;
        csvContent += 'Ledger Code,Ledger Name,Account Type,Group,Debit,Credit\n';
        reportData.entries?.forEach(e => {
          csvContent += `${e.ledger_code},${e.ledger_name},${e.account_type},${e.group_name},${e.debit},${e.credit}\n`;
        });
        csvContent += `\nTotal,,,,${reportData.total_debit},${reportData.total_credit}\n`;
        break;
        
      case 'profit-loss':
        csvContent = 'Profit & Loss Statement\n';
        csvContent += `Period: ${dateRange.from} to ${dateRange.to}\n\n`;
        csvContent += 'INCOME\n';
        csvContent += 'Code,Name,Group,Amount\n';
        reportData.income?.items?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += `Total Income,,,${reportData.income?.total}\n\n`;
        csvContent += 'EXPENSES\n';
        csvContent += 'Code,Name,Group,Amount\n';
        reportData.expenses?.items?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += `Total Expenses,,,${reportData.expenses?.total}\n\n`;
        csvContent += `Net ${reportData.is_profit ? 'Profit' : 'Loss'},,,${Math.abs(reportData.net_profit)}\n`;
        break;
        
      case 'balance-sheet':
        csvContent = 'Balance Sheet\n';
        csvContent += `As of Date: ${dateRange.asOf}\n\n`;
        csvContent += 'ASSETS\n';
        csvContent += 'Current Assets\n';
        csvContent += 'Code,Name,Group,Amount\n';
        reportData.assets?.current_assets?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += '\nFixed Assets\n';
        reportData.assets?.fixed_assets?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += `\nTotal Assets,,,${reportData.assets?.total}\n\n`;
        csvContent += 'LIABILITIES\n';
        reportData.liabilities?.current_liabilities?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += `\nTotal Liabilities,,,${reportData.liabilities?.total}\n\n`;
        csvContent += 'EQUITY\n';
        reportData.equity?.items?.forEach(e => {
          csvContent += `${e.code},${e.name},${e.group},${e.amount}\n`;
        });
        csvContent += `\nTotal Equity,,,${reportData.equity?.total}\n`;
        break;
        
      case 'ledger-statement':
        csvContent = `Ledger Statement - ${reportData.ledger?.name || 'Unknown'}\n`;
        csvContent += `Period: ${dateRange.from} to ${dateRange.to}\n\n`;
        csvContent += 'Date,Narration,Debit,Credit,Balance\n';
        csvContent += `Opening Balance,,,,${reportData.ledger?.opening_balance || 0}\n`;
        
        let totalDebits = 0;
        let totalCredits = 0;
        reportData.transactions?.forEach(t => {
          totalDebits += t.debit || 0;
          totalCredits += t.credit || 0;
          csvContent += `${t.date || ''},"${(t.narration || '').replace(/"/g, '""')}",${t.debit || 0},${t.credit || 0},${t.balance || 0}\n`;
        });
        
        csvContent += `\nTotal Debits & Credits,,${totalDebits},${totalCredits},\n`;
        csvContent += `Closing Balance,,,,${reportData.closing_balance || 0}\n`;
        break;
        
      default:
        toast.error('Export not available for this report type');
        return;
    }
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/finance')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
            <p className="text-gray-600">View and export financial statements</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <Card className="print:hidden">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium">Filter Type:</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Range</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="fy">Financial Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filterType === 'custom' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="fromDate">From Date</Label>
                  <Input
                    id="fromDate"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="toDate">To Date</Label>
                  <Input
                    id="toDate"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="asOfDate">As Of Date</Label>
                  <Input
                    id="asOfDate"
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            {filterType === 'month' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="selectedMonth">Month</Label>
                  <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="selectedYear">Year</Label>
                  <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {filterType === 'fy' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="selectedFY">Financial Year</Label>
                  <Select value={selectedFY} onValueChange={setSelectedFY}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Financial Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {financialYears.map((fy) => (
                        <SelectItem key={fy.value} value={fy.value}>
                          {fy.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button onClick={() => fetchReport(activeTab)} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Apply Filters
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setFilterType('custom');
                  setFromDate('');
                  setToDate(new Date().toISOString().split('T')[0]);
                  setAsOfDate(new Date().toISOString().split('T')[0]);
                  setSelectedMonth(new Date().getMonth() + 1);
                  setSelectedYear(new Date().getFullYear());
                  setSelectedFY('');
                }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="print:hidden">
          <TabsTrigger value="trial-balance" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Trial Balance
          </TabsTrigger>
          <TabsTrigger value="profit-loss" className="flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Profit & Loss
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="ledger-statement" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Ledger Statement
          </TabsTrigger>
        </TabsList>

        {/* Trial Balance */}
        <TabsContent value="trial-balance">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Trial Balance</CardTitle>
              <CardDescription>
                As of {getDateRange().asOf}
                {filterType === 'month' && ` (${months.find(m => m.value === selectedMonth)?.label} ${selectedYear})`}
                {filterType === 'fy' && selectedFY && ` (FY ${selectedFY})`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData?.entries?.length > 0 ? (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Code</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Account Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Group</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Debit (₹)</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Credit (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reportData.entries.map((entry, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">{entry.ledger_code}</td>
                            <td className="px-4 py-3 font-medium">{entry.ledger_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{entry.group_name}</td>
                            <td className="px-4 py-3 text-right font-medium">
                              {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 border-t-2">
                        <tr>
                          <td colSpan="3" className="px-4 py-3 font-bold">Total</td>
                          <td className="px-4 py-3 text-right font-bold">{formatCurrency(reportData.total_debit)}</td>
                          <td className="px-4 py-3 text-right font-bold">{formatCurrency(reportData.total_credit)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {reportData.is_balanced ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <Check className="h-4 w-4 mr-1" />
                        Trial Balance is Balanced
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 border-red-200">
                        <X className="h-4 w-4 mr-1" />
                        Trial Balance Not Balanced
                      </Badge>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Calculator className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No data available for Trial Balance</p>
                  <p className="text-sm">Create journal entries to see data here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit & Loss */}
        <TabsContent value="profit-loss">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Profit & Loss Statement</CardTitle>
              <CardDescription>
                Period: {getDateRange().from || 'Start'} to {getDateRange().to}
                {filterType === 'month' && ` (${months.find(m => m.value === selectedMonth)?.label} ${selectedYear})`}
                {filterType === 'fy' && selectedFY && ` (FY ${selectedFY})`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData ? (
                <div className="space-y-6">
                  {/* Income Section */}
                  <div>
                    <h3 className="font-bold text-lg text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Income
                    </h3>
                    {reportData.income?.items?.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-green-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-green-700">Account</th>
                              <th className="px-4 py-2 text-right text-sm font-medium text-green-700">Amount (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.income.items.map((item, idx) => (
                              <tr key={idx} className="hover:bg-green-50/50">
                                <td className="px-4 py-2">
                                  <span className="text-gray-500 text-sm mr-2">{item.code}</span>
                                  {item.name}
                                </td>
                                <td className="px-4 py-2 text-right font-medium text-green-700">
                                  {formatCurrency(item.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-green-100">
                            <tr>
                              <td className="px-4 py-2 font-bold text-green-800">Total Income</td>
                              <td className="px-4 py-2 text-right font-bold text-green-800">
                                {formatCurrency(reportData.income.total)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No income recorded</p>
                    )}
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <h3 className="font-bold text-lg text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="h-5 w-5" />
                      Expenses
                    </h3>
                    {reportData.expenses?.items?.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-red-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-red-700">Account</th>
                              <th className="px-4 py-2 text-right text-sm font-medium text-red-700">Amount (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {reportData.expenses.items.map((item, idx) => (
                              <tr key={idx} className="hover:bg-red-50/50">
                                <td className="px-4 py-2">
                                  <span className="text-gray-500 text-sm mr-2">{item.code}</span>
                                  {item.name}
                                </td>
                                <td className="px-4 py-2 text-right font-medium text-red-700">
                                  {formatCurrency(item.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-red-100">
                            <tr>
                              <td className="px-4 py-2 font-bold text-red-800">Total Expenses</td>
                              <td className="px-4 py-2 text-right font-bold text-red-800">
                                {formatCurrency(reportData.expenses.total)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No expenses recorded</p>
                    )}
                  </div>

                  {/* Net Profit/Loss */}
                  <div className={`p-6 rounded-lg ${reportData.is_profit ? 'bg-green-100' : 'bg-red-100'}`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`font-bold text-xl ${reportData.is_profit ? 'text-green-800' : 'text-red-800'}`}>
                        Net {reportData.is_profit ? 'Profit' : 'Loss'}
                      </h3>
                      <p className={`text-3xl font-bold ${reportData.is_profit ? 'text-green-800' : 'text-red-800'}`}>
                        {formatCurrency(Math.abs(reportData.net_profit))}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <PieChart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No data available for P&L Statement</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance-sheet">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Balance Sheet</CardTitle>
              <CardDescription>
                As of {getDateRange().asOf}
                {filterType === 'month' && ` (${months.find(m => m.value === selectedMonth)?.label} ${selectedYear})`}
                {filterType === 'fy' && selectedFY && ` (FY ${selectedFY})`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Assets Side */}
                  <div>
                    <h3 className="font-bold text-lg text-blue-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Assets
                    </h3>
                    
                    {/* Current Assets */}
                    {reportData.assets?.current_assets?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Current Assets</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <tbody className="divide-y">
                              {reportData.assets.current_assets.map((item, idx) => (
                                <tr key={idx} className="hover:bg-blue-50/50">
                                  <td className="px-4 py-2 text-sm">{item.name}</td>
                                  <td className="px-4 py-2 text-right font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Fixed Assets */}
                    {reportData.assets?.fixed_assets?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Fixed Assets</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <tbody className="divide-y">
                              {reportData.assets.fixed_assets.map((item, idx) => (
                                <tr key={idx} className="hover:bg-blue-50/50">
                                  <td className="px-4 py-2 text-sm">{item.name}</td>
                                  <td className="px-4 py-2 text-right font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-blue-800">Total Assets</span>
                        <span className="text-xl font-bold text-blue-800">
                          {formatCurrency(reportData.assets?.total)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities & Equity Side */}
                  <div>
                    <h3 className="font-bold text-lg text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="h-5 w-5" />
                      Liabilities & Equity
                    </h3>
                    
                    {/* Current Liabilities */}
                    {reportData.liabilities?.current_liabilities?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Current Liabilities</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <tbody className="divide-y">
                              {reportData.liabilities.current_liabilities.map((item, idx) => (
                                <tr key={idx} className="hover:bg-red-50/50">
                                  <td className="px-4 py-2 text-sm">{item.name}</td>
                                  <td className="px-4 py-2 text-right font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Equity */}
                    {reportData.equity?.items?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Equity</h4>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <tbody className="divide-y">
                              {reportData.equity.items.map((item, idx) => (
                                <tr key={idx} className="hover:bg-purple-50/50">
                                  <td className="px-4 py-2 text-sm">{item.name}</td>
                                  <td className="px-4 py-2 text-right font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-red-100 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-red-800">Total Liabilities & Equity</span>
                        <span className="text-xl font-bold text-red-800">
                          {formatCurrency(reportData.total_liabilities_and_equity)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No data available for Balance Sheet</p>
                </div>
              )}
              
              {reportData && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  {reportData.is_balanced ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <Check className="h-4 w-4 mr-1" />
                      Balance Sheet is Balanced
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-red-200">
                      <X className="h-4 w-4 mr-1" />
                      Balance Sheet Not Balanced (Difference: {formatCurrency(Math.abs((reportData.assets?.total || 0) - (reportData.total_liabilities_and_equity || 0)))})
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ledger Statement */}
        <TabsContent value="ledger-statement">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Ledger Statement</CardTitle>
              <CardDescription>View transaction details for a specific ledger account</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Ledger Selection */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
                <div className="md:col-span-2">
                  <Label>Select Ledger</Label>
                  <Select value={selectedLedgerId} onValueChange={setSelectedLedgerId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a ledger account" />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[300px]">
                        {ledgers.map((ledger) => (
                          <SelectItem key={ledger.id} value={ledger.id}>
                            {ledger.code} - {ledger.name}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-6 print:hidden">
                <Button onClick={fetchLedgerStatement} disabled={!selectedLedgerId}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate Statement
                </Button>
              </div>

              {reportData?.ledger ? (
                <div className="space-y-6">
                  {/* Ledger Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Ledger Code</p>
                        <p className="font-semibold">{reportData.ledger.code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Ledger Name</p>
                        <p className="font-semibold">{reportData.ledger.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Account Type</p>
                        <Badge variant="outline" className="capitalize">{reportData.ledger.account_type}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Opening Balance</p>
                        <p className="font-semibold">{formatCurrency(reportData.ledger.opening_balance)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-700">Date</th>
                          <th className="text-left p-3 font-medium text-gray-700">Voucher No.</th>
                          <th className="text-left p-3 font-medium text-gray-700">Type</th>
                          <th className="text-left p-3 font-medium text-gray-700">Narration</th>
                          <th className="text-right p-3 font-medium text-gray-700">Debit</th>
                          <th className="text-right p-3 font-medium text-gray-700">Credit</th>
                          <th className="text-right p-3 font-medium text-gray-700">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Opening Balance Row */}
                        <tr className="bg-blue-50">
                          <td className="p-3" colSpan={4}>
                            <span className="font-medium text-blue-700">Opening Balance</span>
                          </td>
                          <td className="p-3 text-right">-</td>
                          <td className="p-3 text-right">-</td>
                          <td className="p-3 text-right font-medium text-blue-700">
                            {formatCurrency(reportData.ledger.opening_balance)}
                          </td>
                        </tr>
                        {reportData.transactions?.length > 0 ? (
                          reportData.transactions.map((txn, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="p-3">{new Date(txn.date).toLocaleDateString('en-IN')}</td>
                              <td className="p-3 font-mono text-xs">{txn.voucher_number}</td>
                              <td className="p-3">
                                <Badge variant="outline" className="capitalize text-xs">
                                  {txn.voucher_type?.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="p-3 max-w-xs truncate">{txn.narration || '-'}</td>
                              <td className="p-3 text-right text-green-600">
                                {txn.debit > 0 ? formatCurrency(txn.debit) : '-'}
                              </td>
                              <td className="p-3 text-right text-red-600">
                                {txn.credit > 0 ? formatCurrency(txn.credit) : '-'}
                              </td>
                              <td className="p-3 text-right font-medium">{formatCurrency(txn.balance)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="p-6 text-center text-gray-500">
                              No transactions found for the selected period
                            </td>
                          </tr>
                        )}
                        {/* Closing Balance Row */}
                        <tr className="bg-green-50 border-t-2">
                          <td className="p-3" colSpan={4}>
                            <span className="font-bold text-green-700">Closing Balance</span>
                          </td>
                          <td className="p-3 text-right font-bold text-green-700">
                            {formatCurrency(reportData.transactions?.reduce((sum, t) => sum + t.debit, 0) || 0)}
                          </td>
                          <td className="p-3 text-right font-bold text-green-700">
                            {formatCurrency(reportData.transactions?.reduce((sum, t) => sum + t.credit, 0) || 0)}
                          </td>
                          <td className="p-3 text-right font-bold text-green-700">
                            {formatCurrency(reportData.closing_balance)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : selectedLedgerId ? (
                <div className="text-center py-12 text-gray-500">
                  <RefreshCw className="h-12 w-12 mx-auto mb-3 text-gray-300 animate-spin" />
                  <p>Loading ledger statement...</p>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a ledger account to view its statement</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialReports;
